/**
 * Displays single element within the perdiodic table
 * Child component PeriodicTableRow
 */
import React, { useContext } from "react";
import styled from "styled-components";
import { PeriodicTableContext } from "./PeriodicTableContext";

const PeriodicTableCell = ({ value, index }) => {
  const {
    availableElements,
    availableWithSelectedElements,
    selectedElements,
    updateSelectedElements,
    updateFormulaSearchTerm,
  } = useContext(PeriodicTableContext);

  const isAvailable = availableElements.includes(value);
  const isAvailableWithSelected = availableWithSelectedElements.includes(value);
  const isSelected = selectedElements.includes(value);

  const handleElementClick = (e) => {
    if (!isAvailable) {
      return;
    }

    /**
     * If we are clicking on elements, make sure we don't have a forumla search term being stored
     */
    updateFormulaSearchTerm("");

    /**
     * If cell is clicked but already selected, remove it from the selected elements array
     */
    if (isSelected) {
      const selectedItemsWithoutCurrentlySelectedElement = selectedElements.filter(
        (item) => item !== value
      );
      updateSelectedElements(selectedItemsWithoutCurrentlySelectedElement);
      return;
    }

    /**
     * If cell is clicked and available as a comptabile element, add it to the currently selected elements
     */
    if (isAvailableWithSelected) {
      updateSelectedElements([...selectedElements, value]);
      return;
    }

    /**
     * If cell is clicked, not available as a comptabile element, but it is an available element...
     * clear all other currently selected elements and set this value as the only selected element
     */
    if (isAvailable) {
      updateSelectedElements([value]);
      return;
    }
  };

  /**
   * If an element is available to be selected, set that cell to be an HTML button to receive all the clickable button goodness that comes with being a button.
   * See CellButton being set as styled.button vs styled.div
   */
  if (isAvailable) {
    return (
      <CellButton onClick={handleElementClick}>
        <Element
          className={`
						element
						element--${value}
						${isAvailable ? "element--available" : ""}
						${isAvailableWithSelected ? "element--available-with-selected" : ""}
						${isSelected ? "element--selected" : ""}
						${value === "" ? "element--empty" : ""}
					`}
        >
          <ElementIndex>{index}</ElementIndex>
          <ElementCheckbox
            type="checkbox"
            value={value}
            checked={isSelected ? "checked" : ""}
            readOnly
            tabIndex="-1"
          />
          <ElementValue>{value}</ElementValue>
        </Element>
      </CellButton>
    );
  }

  if (value) {
    return (
      <div>
        <Element className={`element element--${value}`}>
          <ElementIndex>{index}</ElementIndex>
          <ElementValue>{value}</ElementValue>
        </Element>
      </div>
    );
  }

  return (
    <div>
      <Element className={`element element--empty`} />
    </div>
  );
};

export default PeriodicTableCell;

// Styled components
const CellButton = styled.button`
  all: unset;
  box-sizing: border-box;
  transition: all 300ms;

  &:focus,
  &:hover {
    .element:after {
      content: "";
      position: absolute;
      border: 1px solid black;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
`;

const Element = styled.div`
  height: 60px;
  width: 60px;
  display: flex;
  place-content: center;
  place-items: center;
  position: relative;
  border-right: 1px solid black;
  border-bottom: 1px solid black;

  &.element--available {
    cursor: pointer;
    background-color: #cade82;
  }

  &.element--available-with-selected {
    background-color: #cbe8f7;
  }

  &.element--selected {
    background-color: #6cbee7;

    .element__input {
      display: block;
    }
  }

  &.element--empty {
    border-right: 1px solid transparent;
    border-bottom: 1px solid transparent;
  }

  &.element {
    &--H,
    &--He,
    &--Li,
    &--Na,
    &--K,
    &--Rb,
    &--Cs,
    &--Fr,
    &--B,
    &--La,
    &--Ac,
    &--Al {
      border-left: 1px solid black;
    }

    &--H,
    &--Be,
    &--Sc,
    &--Ti,
    &--V,
    &--Cr,
    &--Mn,
    &--Fe,
    &--Co,
    &--Ni,
    &--Cu,
    &--Zn,
    &--B,
    &--C,
    &--N,
    &--O,
    &--F,
    &--He,
    &--La,
    &--Ce,
    &--Pr,
    &--Nd,
    &--Pm,
    &--Sm,
    &--Eu,
    &--Gd,
    &--Tb,
    &--Dy,
    &--Ho,
    &--Er,
    &--Tm,
    &--Yb,
    &--Lu {
      border-top: 1px solid black;
    }
  }
`;

const ElementValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  padding: 1px 4px;
`;

const ElementCheckbox = styled.input.attrs({ type: "checkbox" })`
  position: absolute;
  display: none;
  width: 15px;
  height: 15px;
  right: 0;
  top: 0;

  &:checked {
    background-color: #fff;
    display: block;
  }
`;

const ElementIndex = styled.div`
  position: absolute;
  left: 2px;
  top: 2px;
  font-size: 11px;
  line-height: 1;
`;
